import { useState, forwardRef } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";
import TextInput from "./TextInput";
import styles from "./PasswordField.module.css";

const calculateStrength = (pass) => {
  if (!pass) return 0;
  let score = 0;
  if (pass.length >= 8) score += 1;
  if (/[A-Z]/.test(pass)) score += 1;
  if (/[0-9]/.test(pass)) score += 1;
  if (/[^A-Za-z0-9]/.test(pass)) score += 1;
  return score;
};

const STRENGTH_LABELS = ["", "Weak", "Fair", "Good", "Strong"];
const STRENGTH_COLORS = ["", "#e12b2b", "#f59f1b", "#3b7fd6", "#36c4a3"];

const PasswordField = forwardRef(
  (
    {
      value = "",
      onChange,
      name,
      id,
      placeholder = "Enter password",
      showStrengthIndicator = false,
      leadIcon = <Lock size={16} />,
      disabled = false,
      error = false,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);

    const toggleVisibility = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setShowPassword((prev) => !prev);
    };

    const strength = showStrengthIndicator ? calculateStrength(value) : 0;

    return (
      <div style={{ width: "100%" }}>
        <TextInput
          ref={ref}
          id={id || name}
          name={name}
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          leadIcon={leadIcon}
          disabled={disabled}
          error={error}
          trailIcon={
            <button
              type="button"
              className={styles.toggleBtn}
              onClick={toggleVisibility}
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
              disabled={disabled}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          }
          {...props}
        />

        {showStrengthIndicator && value.length > 0 && (
          <div className={styles.strengthBarContainer}>
            <div className={styles.strengthTracks}>
              {[1, 2, 3, 4].map((step) => (
                <div
                  key={step}
                  className={styles.strengthSegment}
                  style={{
                    backgroundColor:
                      strength >= step ? STRENGTH_COLORS[strength] : undefined,
                  }}
                />
              ))}
            </div>
            <span
              className={styles.strengthLabel}
              style={{ color: STRENGTH_COLORS[strength] }}
            >
              Password strength: {STRENGTH_LABELS[strength]}
            </span>
          </div>
        )}
      </div>
    );
  }
);

PasswordField.displayName = "PasswordField";

export default PasswordField;
